import React, { useCallback, useEffect } from 'react';
import {
  Box,
  VStack,
  Heading,
  Text,
  SimpleGrid,
  Spinner,
  StackDivider,
  Flex
} from '@chakra-ui/react';
import {
  MdPersonAdd,
  MdQueue,
  MdCheckCircle,
  MdPersonOutline,
  MdPhone,
  MdPersonOff,
  MdCall,
  MdError
} from 'react-icons/md';
import { useDispatch, useSelector } from 'react-redux';
import { fetchOverallOutboundCallingStats } from '@store/actions/outboundCallingActions';
import {
  OUTBOUND_CALLING_DASHBOARD_STRINGS,
  STATUS_CARD_IDS,
  TAB_STATES,
  OUTBOUND_CALLING_ROUTES
} from '../constant';
import StatusCard from '@components/FalconServiceDashboard/StatusCount/StatusCard';
import {
  COLOR_SCHEMES,
  getCallSupportOverviewData,
  getAgentAvailabilityData,
  getCallAnalyticsData
} from '@views/OutboundCallingDashboard/utils';
import { useNavigate } from 'react-router-dom';


/**
 * Icon mapping - Maps string icon names to actual icon components
 */
const ICON_MAP = {
  MdPersonAdd,
  MdQueue,
  MdCheckCircle,
  MdPersonOutline,
  MdPhone,
  MdPersonOff,
  MdCall,
  MdError
};

/**
 * Admin Dashboard Component
 */
const AdminDashboard = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const dashboardStringConstants =
    OUTBOUND_CALLING_DASHBOARD_STRINGS.STRING_CONSTANTS[TAB_STATES.ADMIN];

  // Redux state
  // Admin Dashboard Data
  const {
    data: dashboardData,
    isLoading,
    error
  } = useSelector(
    (state) =>
      state.outboundCalling?.adminDashboard?.stats || {
        data: null,
        isLoading: false,
        error: null
      }
  );

  /**
   * Fetch admin dashboard statistics
   */
  const fetchAdminScreenStats = useCallback(() => {
    dispatch(fetchOverallOutboundCallingStats());
  }, [dispatch]);

  /**
   * Fetch dashboard data on mount
   */
  useEffect(() => {
    fetchAdminScreenStats();
  }, [dispatch]);

  /**
   * Helper: Get icon component from string name
   */
  const getIconComponent = useCallback((iconName) => {
    return ICON_MAP[iconName] || null;
  }, []);

  /**
   * Helper: Create stat data for StatusCard
   */
  const createStatData = useCallback(
    (id, count, title) => ({
      status: id,
      count: count || 0,
      title
    }),
    []
  );

  /**
   * Render loading state
   */
  const renderLoadingState = () => (
    <Flex justify="center" align="center" w="full" h="200px">
      <VStack spacing={3}>
        <Spinner thickness="4px" size="lg" color="colorPrimary" />
        <Text fontSize="sm" color="whiteAlpha.600">
          {dashboardStringConstants.LOADING_ADMIN_STATS}
        </Text>
      </VStack>
    </Flex>
  );

  /**
   * Render error state
   */
  const renderErrorState = () => (
    <Box
      p={8}
      bg="backGround"
      border="1px solid"
      borderColor="red.500"
      borderRadius="xl"
      textAlign="center"
    >
      <Text color="red.400" fontSize="lg" fontWeight="bold">
        {dashboardStringConstants.ERROR_LOADING_STATS}
      </Text>
      <Text color="whiteAlpha.500" fontSize="sm" mt={2}>
        {error || dashboardStringConstants.NO_STATS_MESSAGE}
      </Text>
    </Box>
  );

  /**
   * Render empty state
   */
  const renderEmptyState = () => (
    <Box
      p={8}
      bg="backGround"
      border="1px solid"
      borderColor="whiteAlpha.300"
      borderRadius="xl"
      textAlign="center"
    >
      <Text color="whiteAlpha.700" fontSize="lg">
        {dashboardStringConstants.NO_STATS_FOUND}
      </Text>
      <Text color="whiteAlpha.500" fontSize="sm" mt={2}>
        {dashboardStringConstants.NO_STATS_MESSAGE}
      </Text>
    </Box>
  );

  /**
   * Render call support overview section
   */
  const renderCallSupportOverview = () => {
    const overviewData = getCallSupportOverviewData(dashboardData);
    const handleClick = (id) => {
      const navigateTo = {
        [STATUS_CARD_IDS.AVAILABLE_AGENTS]: OUTBOUND_CALLING_ROUTES.AGENTS_LIST,
        [STATUS_CARD_IDS.QUEUE_LENGTH]: OUTBOUND_CALLING_ROUTES.TICKETS_QUEUE,
        [STATUS_CARD_IDS.COMPLETED_CALLS]: OUTBOUND_CALLING_ROUTES.CLOSED_TICKETS
      };

      if (navigateTo[id]) {
        navigate(navigateTo[id]);
      }
    };

    return (
      <VStack align="stretch" spacing={4}>
        <Heading size="md">
          {dashboardStringConstants.CALL_SUPPORT_OVERVIEW}
        </Heading>
        <SimpleGrid columns={{ base: 1, md: 3 }} spacing={6}>
          {overviewData.map((stat) => (
            <StatusCard
              key={stat.id}
              status={createStatData(stat.id, stat.count, stat.title)}
              icon={getIconComponent(stat.icon)}
              activeColor={COLOR_SCHEMES[stat.colorScheme]}
              description={stat.description}
              onClick={() => handleClick(stat.id)}
            />
          ))}
        </SimpleGrid>
      </VStack>
    );
  };

  /**
   * Render agent availability section
   */
  const renderAgentAvailability = () => {
    const availabilityData = getAgentAvailabilityData(dashboardData);

    return (
      <VStack align="stretch" spacing={4}>
        <Heading size="md" color="white">
          {dashboardStringConstants.AGENT_AVAILABILITY}
        </Heading>
        <SimpleGrid columns={{ base: 1, md: 3 }} spacing={6}>
          {availabilityData.map((stat) => (
            <StatusCard
              key={stat.id}
              status={createStatData(stat.id, stat.count, stat.title)}
              icon={getIconComponent(stat.icon)}
              activeColor={COLOR_SCHEMES[stat.colorScheme]}
              description={stat.description}
            />
          ))}
        </SimpleGrid>
      </VStack>
    );
  };

  /**
   * Render call analytics section
   */
  const renderCallAnalytics = () => {
    const analyticsData = getCallAnalyticsData(dashboardData);

    return (
      <VStack align="stretch" spacing={4}>
        <Heading size="md" color="white">
          {dashboardStringConstants.CALL_ANALYTICS}
        </Heading>
        <SimpleGrid columns={{ base: 1, md: 3 }} spacing={6}>
          {analyticsData.map((stat) => (
            <StatusCard
              key={stat.id}
              status={createStatData(stat.id, stat.count, stat.title)}
              icon={getIconComponent(stat.icon)}
              activeColor={COLOR_SCHEMES[stat.colorScheme]}
              description={stat.description}
            />
          ))}
        </SimpleGrid>
      </VStack>
    );
  };

  /**
   * Main content renderer
   */
  const renderContent = () => {
    if (isLoading) return renderLoadingState();
    if (error) return renderErrorState();
    if (!dashboardData) return renderEmptyState();

    return (
      <VStack align="stretch" spacing={8}>
        {renderCallSupportOverview()}
        {renderAgentAvailability()}
        {renderCallAnalytics()}
      </VStack>
    );
  };

  return (
    <VStack
      overflowY={{ base: 'initial', lg: 'scroll' }}
      overflowX="hidden"
      alignItems="stretch"
      gap={4}
      px={4}
      divider={<StackDivider borderColor="whiteAlpha.300" />}
    >
      {/* Dashboard Content */}
      {renderContent()}
    </VStack>
  );
};

export default AdminDashboard;
export { default as OutboundCallingAgentsDashboard } from './AgentsDashboard';
export { default as OutboundCallingOpenTicketsDashboard } from './OpenTicketsDashboard';
export { default as OutboundCallingClosedTicketsDashboard } from './ClosedTicketsDashboard';
